import { Card, CardContent } from '../components/ui/card';
import { FadeIn, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import { IMAGES } from '../lib/images';
import { Building2, CreditCard, Zap, Shield, Heart, Users, Lightbulb, Handshake } from 'lucide-react';



const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-14 sm:pb-16">
    <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(1200px circle at 80% 10%, rgba(242,107,91,0.08), transparent 55%)' }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>About Us</SectionLabel>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            We Help Dental Practices Recover the Revenue They're Losing to Financing Declines
          </h1>
          <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">
            Smile Score Solutions was built for one reason: to solve the problem every dental practice knows but nobody had a real solution for — what happens to the patient who wants treatment but can't get approved.
          </p>
        </FadeInLeft>
        <FadeInRight>
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-64 sm:h-80">
            <img src={IMAGES.dentistExamining} alt="Successful team" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

const MissionSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>Our Mission</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">The Problem We Set Out to Solve</h2>
          <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">Every day, dental practices across the country present treatment plans to patients who are ready to move forward. Then the financing application comes back declined. The patient leaves disappointed. The practice loses the case. And in most offices, that's the end of the story.</p>
          <p className="mt-3 text-base font-medium text-[hsl(var(--sss-navy))]">We believed there had to be a better way.</p>
          <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">Smile Score Solutions was created to give dental practices a systematic, intelligent way to stay connected with declined patients and help them work toward financial readiness.</p>
          <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">We're a financial wellness platform that gives your team a recovery system for patients who aren't ready to qualify today but could be ready in 30, 60, or 90 days.</p>
        </FadeInLeft>
        <FadeInRight>
          <div className="relative rounded-2xl overflow-hidden shadow-[var(--shadow-lg)]">
            <img src={IMAGES.dentalExplaining} alt="Dentist explaining treatment" className="w-full h-auto object-cover rounded-2xl" loading="lazy" />
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

const DifferentSection = () => {
  const features = [
    { icon: <Building2 className="h-5 w-5" />, title: 'Practice-First Design', desc: 'Everything about Smile Score Solutions is designed for how dental practices actually work. The portal is branded to your practice. The workflows fit into your existing patient communication. This was built from the ground up for dental practices.' },
    { icon: <CreditCard className="h-5 w-5" />, title: 'Top Bureau Credit Data', desc: 'Our credit monitoring is powered by data from top credit bureaus, giving patients accurate, reliable score tracking and giving your practice confidence in the data.' },
    { icon: <Zap className="h-5 w-5" />, title: 'Intelligent Recommendation Engine', desc: 'Our system delivers personalized improvement recommendations to each patient based on their unique credit profile. Patients receive specific action steps, projected timelines, and estimated score impact.' },
    { icon: <Shield className="h-5 w-5" />, title: 'Compliance-Conscious Approach', desc: 'We operate with deep respect for data privacy, consumer financial regulations, and healthcare industry standards. Our platform is designed to be compliant, professional, and trustworthy.' },
  ];
  return (
    <section className="relative py-16 sm:py-20 bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <FadeInLeft>
              <SectionLabel>What Makes Us Different</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Built for Dentistry. Powered by Data.</h2>
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-48">
                  <img src={IMAGES.dentalOffice} alt="Modern dental office" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-sm)] h-40">
                  <img src={IMAGES.happySmileCloseup} alt="Happy patient" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </FadeInLeft>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {features.map((f, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal))] flex items-center justify-center shrink-0">{f.icon}</div>
                      <div>
                        <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-1">{f.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ValuesSection = () => {
  const values = [
    { icon: <Heart className="h-5 w-5" />, title: 'Every Patient Deserves a Second Chance', desc: 'A financing decline shouldn\'t be the end of a patient\'s journey. With the right tools, many patients can improve their standing and return for care.' },
    { icon: <Users className="h-5 w-5" />, title: 'Practices Deserve Better Tools', desc: 'Dental practices spend thousands acquiring patients. When those patients are declined, practices deserve a system — not a shrug.' },
    { icon: <Lightbulb className="h-5 w-5" />, title: 'Technology Should Be Invisible', desc: 'The best technology works in the background. Our platform is powered by intelligent systems, but what patients experience is simple and helpful.' },
    { icon: <Handshake className="h-5 w-5" />, title: 'Trust Is Earned', desc: 'We earn trust through transparency, results, and a relentless focus on doing right by both practices and patients.' },
  ];
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center max-w-3xl mx-auto mb-12">
          <SectionLabel>Our Values</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">What We Believe</h2>
        </FadeIn>
        <StaggerContainer className="grid gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
          {values.map((v, i) => (
            <StaggerItem key={i}>
              <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal))] flex items-center justify-center mb-4">{v.icon}</div>
                  <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-2">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default function AboutPage() {
  return (
    <div>
      <HeroSection />
      <MissionSection />
      <DifferentSection />
      <ValuesSection />
      <CTASection headline="Partner With Us" subheadline="Join the growing number of dental practices using Smile Score Solutions to recover lost cases and grow production." />
    </div>
  );
}
